<script setup lang="ts">
/**
 * Lifestyle quiz — full-screen layer (Figma Quizzes › Lifestyle quiz · desktop
 * 3229:105744 / mobile 3229:106519). The answers are the user's own, so no AI mark.
 * Every question is optional and applied as the step is left (R203); × asks first, but
 * nothing is lost (09 note).
 */
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { LIFESTYLE_SURVEY_KEY, putSurveyResponse } from '@/features/survey/api/surveys'
import { queryCache } from '@/queries'
import { useUserLifestyleQuery } from '@/queries/survey'
import { PROFILE_MY_STYLE_ROUTE } from '@/router/routes'
import {
  FairQuizHeader,
  FairQuizLayer,
  FairQuizLeaveDialog,
  FairQuizStep,
  FairQuizSummary,
  FairQuizWelcome,
  settleFairQuizHistory,
  type FairQuizSummaryRow,
} from '@/ui/patterns/FairQuiz'
import { FairInputText } from '@/ui/primitives/FairInputText'
import { FairThemeCard } from '@/ui/primitives/FairThemeCard'
import { useLifestyleQuizStore } from '../store'
import {
  LIFESTYLE_QUIZ_QUESTIONS,
  emptyLifestyleQuizState,
  lifestyleQuizProgress,
  lifestyleQuizResponseBody,
  lifestyleQuizStateFrom,
  lifestyleQuizSteps,
  toggleLifestyleQuizAnswer,
  type LifestyleQuizState,
  type LifestyleQuizStep,
} from '../lifestyle-quiz.data'
import './lifestyle-quiz.css'

const store = useLifestyleQuizStore()
const { t } = useI18n()
const router = useRouter()
const lifestyleQuery = useUserLifestyleQuery(computed(() => store.isOpen))

/** The server does not know the new questions yet (BE-152): what it refuses is kept here. */
const LOCAL_KEY = 'fair.lifestyle-quiz'

const state = ref<LifestyleQuizState>(emptyLifestyleQuizState())
const step = ref<LifestyleQuizStep>('welcome')
const steps = computed(() => lifestyleQuizSteps(state.value))
const question = computed(() => LIFESTYLE_QUIZ_QUESTIONS.find((item) => item.id === step.value) ?? null)

function readLocal(): LifestyleQuizState | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(LOCAL_KEY)
    return raw ? (JSON.parse(raw) as LifestyleQuizState) : null
  } catch {
    return null
  }
}

function writeLocal(value: LifestyleQuizState | null) {
  if (typeof window === 'undefined') return
  try {
    if (value) window.localStorage.setItem(LOCAL_KEY, JSON.stringify(value))
    else window.localStorage.removeItem(LOCAL_KEY)
  } catch {
    // Private mode — the answers still went to the server, or are lost with the tab.
  }
}

/** Applies the answers so far (R203). */
async function apply() {
  try {
    await putSurveyResponse(LIFESTYLE_SURVEY_KEY, lifestyleQuizResponseBody(state.value, steps.value))
    writeLocal(null)
    queryCache.invalidate(['user-lifestyle'])
    queryCache.invalidate(['user-profile'])
  } catch (error) {
    console.warn('Lifestyle quiz: the server did not take the answers, kept locally (BE-152)', error)
    writeLocal(state.value)
  }
}

function move(by: 1 | -1) {
  const index = steps.value.indexOf(step.value)
  const next = steps.value[Math.max(0, Math.min(steps.value.length - 1, index + by))]
  if (next) step.value = next
}

function leaveStep(by: 1 | -1) {
  if (step.value !== 'welcome' && step.value !== 'result') void apply()
  move(by)
}

function choose(value: string) {
  const current = question.value
  if (!current) return
  state.value = {
    ...state.value,
    answers: {
      ...state.value.answers,
      [current.id]: toggleLifestyleQuizAnswer(state.value.answers[current.id], value, current.multi),
    },
    // Choosing a card other than Other clears the typed text (R171).
    custom: current.customValue && value !== current.customValue ? '' : state.value.custom,
  }
}

const showCustom = computed(
  () => Boolean(question.value?.customValue) && state.value.answers[question.value!.id]?.[0] === question.value?.customValue,
)

/* ── Result ───────────────────────────────────────────────────────────── */

function labelOf(questionId: string, value: string): string {
  return t(`lifestyle_quiz.questions.${questionId}.options.${value}`)
}

const resultRows = computed<FairQuizSummaryRow[]>(() => {
  const rows: FairQuizSummaryRow[] = LIFESTYLE_QUIZ_QUESTIONS.filter((item) => steps.value.includes(item.id))
    .map((item) => {
      const chosen = state.value.answers[item.id] ?? []
      const value =
        item.customValue && chosen[0] === item.customValue && state.value.custom.trim()
          ? state.value.custom.trim()
          : chosen.map((option) => labelOf(item.id, option)).join(', ')
      return { key: item.id, label: t(`lifestyle_quiz.result.${item.id}`), value }
    })
  rows.push({ key: 'note', label: t('lifestyle_quiz.result.note'), value: state.value.note.trim() })
  return rows
})

async function openStyle() {
  store.close()
  await settleFairQuizHistory()
  await router.push(PROFILE_MY_STYLE_ROUTE)
}

/* ── Header + leaving ─────────────────────────────────────────────────── */

const progress = computed(() => lifestyleQuizProgress(steps.value, step.value))
const headerRight = computed(() => (step.value === 'welcome' || step.value === 'result' ? 'none' : 'skip'))
const leaveOpen = ref(false)

function requestClose() {
  if (step.value === 'welcome' || step.value === 'result') {
    store.close()
    return
  }
  leaveOpen.value = true
}

async function exit() {
  leaveOpen.value = false
  await apply()
  store.close()
}

/* ── Open ─────────────────────────────────────────────────────────────── */

watch(
  () => store.session,
  async () => {
    step.value = 'welcome'
    await lifestyleQuery.refresh(true)
    const stored = lifestyleQuizStateFrom(lifestyleQuery.data.value)
    const hasStored = Object.keys(stored.answers).length > 0 || Boolean(stored.note)
    state.value = hasStored ? stored : readLocal() ?? emptyLifestyleQuizState()
  },
  { immediate: true },
)
</script>

<template>
  <FairQuizLayer
    :open="store.isOpen"
    class="lifestyle-quiz"
    :aria-label="t('lifestyle_quiz.aria')"
    @close-request="requestClose"
  >
    <FairQuizHeader
      :progress="progress"
      :right="headerRight"
      :skip-label="t('quiz.skip')"
      :close-label="t('quiz.close')"
      :progress-label="t('quiz.progress')"
      @close="requestClose"
      @skip="leaveStep(1)"
    />

    <FairQuizWelcome
      v-if="step === 'welcome'"
      :title="t('lifestyle_quiz.welcome.title')"
      :subtitle="t('lifestyle_quiz.welcome.subtitle')"
      :pills="[{ label: t('lifestyle_quiz.welcome.pill_time'), star: false }]"
      :continue-label="t('quiz.continue')"
      @continue="move(1)"
    />

    <!-- 01–06 Theme card questions -->
    <FairQuizStep
      v-else-if="question"
      fill
      :title="t(`lifestyle_quiz.questions.${question.id}.title`)"
      :subtitle="question.multi ? t('lifestyle_quiz.pick_any') : t('lifestyle_quiz.pick_one')"
      :show-back="step !== 'occupation'"
      :back-label="t('quiz.back')"
      :continue-label="t('quiz.continue')"
      @back="leaveStep(-1)"
      @continue="leaveStep(1)"
    >
      <div class="lifestyle-quiz__content">
        <div
          class="lifestyle-quiz__cards"
          :class="{ 'lifestyle-quiz__cards--icons': question.options.some((option) => option.icon) }"
          :role="question.multi ? 'group' : 'radiogroup'"
          :aria-label="t(`lifestyle_quiz.questions.${question.id}.title`)"
        >
          <FairThemeCard
            v-for="option in question.options"
            :key="option.value"
            :label="labelOf(question.id, option.value)"
            :icon="option.icon"
            :multi="question.multi"
            :selected="(state.answers[question.id] ?? []).includes(option.value)"
            @click="choose(option.value)"
          />
        </div>
        <FairInputText
          v-if="showCustom"
          v-model="state.custom"
          variant="underline"
          :placeholder="t(`lifestyle_quiz.questions.${question.id}.custom`)"
        />
      </div>
    </FairQuizStep>

    <!-- 07 Note -->
    <FairQuizStep
      v-else-if="step === 'note'"
      fill
      :title="t('lifestyle_quiz.note.title')"
      :subtitle="t('lifestyle_quiz.note.subtitle')"
      :back-label="t('quiz.back')"
      :continue-label="t('lifestyle_quiz.note.done')"
      @back="leaveStep(-1)"
      @continue="leaveStep(1)"
    >
      <FairInputText
        v-model="state.note"
        variant="underline"
        :placeholder="t('lifestyle_quiz.note.placeholder')"
      />
    </FairQuizStep>

    <!-- 08 Result: plain text, no AI mark -->
    <FairQuizStep
      v-else-if="step === 'result'"
      :title="t('lifestyle_quiz.result.title')"
      :subtitle="t('lifestyle_quiz.result.subtitle')"
      :show-back="false"
      :continue-label="t('lifestyle_quiz.result.open_style')"
      @continue="openStyle"
    >
      <FairQuizSummary :rows="resultRows" />
    </FairQuizStep>

    <FairQuizLeaveDialog
      v-model:visible="leaveOpen"
      :title="t('lifestyle_quiz.leave.title')"
      :text="t('lifestyle_quiz.leave.text')"
      :keep-label="t('lifestyle_quiz.leave.keep')"
      :exit-label="t('lifestyle_quiz.leave.exit')"
      :close-label="t('quiz.close')"
      @exit="exit"
    />
  </FairQuizLayer>
</template>
